/**
 * Service for reading and decrypting entries from the kv collection
 * Each decryption key request has three entries that need to be decrypted with auditor's private key
 */

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ethers } from 'ethers';
import { Buffer } from 'buffer';
import { aggregateKeyshares } from '../utils/keyshareAggregation';

const KV_COLLECTION = 'kv';

/**
 * Query kv collection for entries related to a decryption key request
 * Entries are identified by patterns like dkreq/dlq/... or dkreq/pending/...
 * Document IDs are base64-encoded paths
 * @param {string} auditorIdentity - Auditor UID
 * @param {string} userAddress - User's wallet address
 * @returns {Promise<Array>} Array of entry documents with k and _byteString fields
 */
export const getKvEntriesForRequest = async (auditorIdentity, userAddress) => {
  try {
    const normalizedAuditor = auditorIdentity.toLowerCase();
    const normalizedUser = userAddress.toLowerCase();

    // The three entries have paths:
    // - dkreq/dlq/{auditorIdentity}/{userAddress}
    // - dkreq/pending/{auditorIdentity}/{userAddress}
    // - dkreq/success/{auditorIdentity}/{userAddress}
    const pathPatterns = [
      `dkreq/dlq/${normalizedAuditor}/${normalizedUser}`,
      `dkreq/pending/${normalizedAuditor}/${normalizedUser}`,
      `dkreq/success/${normalizedAuditor}/${normalizedUser}`
    ];
    
    const matchingEntries = [];
    const kvCollection = collection(db, KV_COLLECTION);
    
    // First, try to get documents by base64-encoded document IDs
    for (const pathPattern of pathPatterns) {
      try {
        // Document IDs are base64-encoded paths
        const encodedId = Buffer.from(pathPattern).toString('base64');
        const docRef = doc(db, KV_COLLECTION, encodedId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          matchingEntries.push({
            id: docSnap.id,
            k: data.k || pathPattern,
            _byteString: data._byteString,
            path: pathPattern,
            ...data
          });
        }
      } catch (e) {
        console.warn(`Failed to get document for path ${pathPattern}:`, e);
      }
    }
    
    // If we didn't find all entries by document ID, try querying by k field
    if (matchingEntries.length < 3) {
      // Query all documents and filter by k field
      const allDocsQuery = query(kvCollection);
      const snapshot = await getDocs(allDocsQuery);
      
      const foundPaths = new Set(matchingEntries.map(e => e.path));
      
      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const kValue = data.k;
        
        if (kValue && typeof kValue === 'string') {
          // Check if this entry matches any of our search patterns
          for (const pattern of pathPatterns) {
            if (!foundPaths.has(pattern) && 
                (kValue === pattern || kValue.includes(pattern) || kValue.endsWith(pattern))) {
              matchingEntries.push({
                id: docSnapshot.id,
                k: kValue,
                _byteString: data._byteString,
                path: pattern,
                ...data
              });
              foundPaths.add(pattern);
              break;
            }
          }
        }
      });
    }
    
    // Sort entries by path to ensure consistent order
    matchingEntries.sort((a, b) => {
      const order = ['dlq', 'pending', 'success'];
      const aStatus = a.path?.split('/')[1] || '';
      const bStatus = b.path?.split('/')[1] || '';
      return order.indexOf(aStatus) - order.indexOf(bStatus);
    });
    
    return matchingEntries;
  } catch (error) {
    console.error('Error querying kv entries:', error);
    throw new Error(`Failed to query kv entries: ${error.message}`);
  }
};

// btcec.Encrypt format: IV(16) | PublicKey(70) | Ciphertext | HMAC(32); PublicKey = curve(2)=0x02CA + lenX(2)=0x0020 + X(32) + lenY(2)=0x0020 + Y(32)
const CIPH_CURVE_BYTES = Buffer.from([0x02, 0xca]);
const CIPH_COORD_LENGTH = Buffer.from([0x00, 0x20]);

/**
 * Returns true if raw ciphertext looks like btcec.Encrypt output (starts with IV then 0x02 0xCA curve bytes).
 */
function isBtcecFormat(raw) {
  if (!Buffer.isBuffer(raw) || raw.length < 16 + 70 + 32) return false;
  return raw[16] === CIPH_CURVE_BYTES[0] && raw[17] === CIPH_CURVE_BYTES[1];
}

/**
 * Decrypt btcec.Encrypt output in the browser using Web Crypto API (no Node crypto).
 * Format: IV(16) | PublicKey(70) | Ciphertext | HMAC(32). Key derivation: ECDH + SHA-512; MAC: HMAC-SHA256.
 * @param {Buffer} privateKeyBuffer - 32-byte SECP256k1 private key
 * @param {Buffer} raw - Full ciphertext buffer
 * @returns {Promise<Buffer>} Plaintext
 */
async function decryptBtcec(privateKeyBuffer, raw) {
  if (!Buffer.isBuffer(raw) || raw.length < 16 + 70 + 16 + 32) {
    throw new Error('btcec ciphertext too short');
  }
  const iv = raw.subarray(0, 16);
  let offset = 16;
  if (raw.subarray(offset, offset + 2).compare(CIPH_CURVE_BYTES) !== 0) throw new Error('unsupported curve');
  offset += 2;
  if (raw.subarray(offset, offset + 2).compare(CIPH_COORD_LENGTH) !== 0) throw new Error('invalid X length');
  offset += 2;
  const xBytes = raw.subarray(offset, offset + 32);
  offset += 32;
  if (raw.subarray(offset, offset + 2).compare(CIPH_COORD_LENGTH) !== 0) throw new Error('invalid Y length');
  offset += 2;
  const yBytes = raw.subarray(offset, offset + 32);
  offset += 32;
  const ephemPublicKey = Buffer.allocUnsafe(65);
  ephemPublicKey[0] = 0x04;
  xBytes.copy(ephemPublicKey, 1);
  yBytes.copy(ephemPublicKey, 33);

  const ciphertext = raw.subarray(offset, raw.length - 32);
  const messageMAC = raw.subarray(raw.length - 32);

  let eccrypto;
  try {
    eccrypto = require('eccrypto');
  } catch (e) {
    const m = await import('eccrypto');
    eccrypto = m.default || m;
  }
  const ecdhKey = await eccrypto.derive(privateKeyBuffer, ephemPublicKey);
  let ecdhBytes = ecdhKey.buffer && ecdhKey.byteOffset !== undefined
    ? new Uint8Array(ecdhKey.buffer, ecdhKey.byteOffset, ecdhKey.length)
    : new Uint8Array(ecdhKey);
  // Go's btcec derives the shared secret as GenerateSharedSecret -> big.Int.Bytes(), i.e. the
  // MINIMAL big-endian encoding of the X coordinate with NO leading zero bytes, and hashes that
  // with SHA-512. Padding to a fixed 32 bytes would change the SHA-512 preimage whenever X has a
  // leading zero byte, causing an "invalid mac hash" failure (happens ~1/256 of the time). Match
  // Go by using the minimal representation: drop any leading zero bytes.
  if (ecdhBytes.length > 32) {
    ecdhBytes = ecdhBytes.subarray(ecdhBytes.length - 32);
  }
  let ecdhStart = 0;
  while (ecdhStart < ecdhBytes.length - 1 && ecdhBytes[ecdhStart] === 0) ecdhStart++;
  const ecdhForHash = ecdhBytes.subarray(ecdhStart);
  const derivedKey = await crypto.subtle.digest('SHA-512', ecdhForHash);
  const derived = new Uint8Array(derivedKey);
  const keyE = derived.subarray(0, 32);
  const keyM = derived.subarray(32);

  const dataToMac = raw.subarray(0, raw.length - 32);
  const macKey = await crypto.subtle.importKey('raw', keyM, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const expectedMAC = await crypto.subtle.sign('HMAC', macKey, dataToMac);
  const expectedMACArr = new Uint8Array(expectedMAC);
  if (expectedMACArr.length !== messageMAC.length) throw new Error('invalid mac hash');
  let macMatch = true;
  for (let i = 0; i < messageMAC.length; i++) {
    if (messageMAC[i] !== expectedMACArr[i]) { macMatch = false; break; }
  }
  if (!macMatch) throw new Error('invalid mac hash');

  const aesKey = await crypto.subtle.importKey('raw', keyE, 'AES-CBC', false, ['decrypt']);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv: new Uint8Array(iv) },
    aesKey,
    ciphertext
  );
  const decryptedArr = new Uint8Array(decrypted);
  const lastByte = decryptedArr[decryptedArr.length - 1];
  if (lastByte >= 1 && lastByte <= 16 && decryptedArr.length >= lastByte) {
    let validPadding = true;
    for (let i = 1; i <= lastByte; i++) {
      if (decryptedArr[decryptedArr.length - i] !== lastByte) { validPadding = false; break; }
    }
    if (validPadding) {
      return Buffer.from(decryptedArr.subarray(0, decryptedArr.length - lastByte));
    }
  }
  return Buffer.from(decryptedArr);
}

/**
 * Parse serialized ECIES ciphertext into the object eccrypto.decrypt expects.
 * Layout (same as eccrypto/btcec-style): [ephemPublicKey][iv (16)][ciphertext][mac (32)]
 * Ephemeral key: 65 bytes if first byte 0x04 (uncompressed), 33 bytes if 0x02/0x03 (compressed).
 * @param {Buffer} raw - Raw encrypted buffer
 * @returns {{ iv: Buffer, ephemPublicKey: Buffer, ciphertext: Buffer, mac: Buffer } | null}
 */
function parseEciesSerialized(raw) {
  if (!Buffer.isBuffer(raw) || raw.length < 81) return null; // min: 33+16+0+32
  const first = raw[0];
  const ephemLen = (first === 0x04) ? 65 : (first === 0x02 || first === 0x03) ? 33 : null;
  if (!ephemLen || raw.length < ephemLen + 16 + 32) return null;
  return {
    ephemPublicKey: Buffer.from(raw.subarray(0, ephemLen)),
    iv: Buffer.from(raw.subarray(ephemLen, ephemLen + 16)),
    ciphertext: Buffer.from(raw.subarray(ephemLen + 16, raw.length - 32)),
    mac: Buffer.from(raw.subarray(raw.length - 32)),
  };
}

/**
 * Decrypt ECIES-encrypted data using SECP256k1 private key.
 * eccrypto.decrypt expects an object { iv, ephemPublicKey, ciphertext, mac }, not a raw Buffer.
 * We accept either hex string (serialized) or that object; serialized layout: [ephemPublicKey][iv][ciphertext][mac].
 * @param {string} encryptedHex - Hex-encoded serialized ECIES data
 * @param {string} privateKeyHex - SECP256k1 private key in hex format (with or without 0x)
 * @returns {Promise<string>} Decrypted plaintext as string
 */
export const decryptEciesData = async (encryptedHex, privateKeyHex) => {
  try {
    const cleanPrivateKey = privateKeyHex.startsWith('0x') ? privateKeyHex.slice(2) : privateKeyHex;
    const cleanEncrypted = encryptedHex.startsWith('0x') ? encryptedHex.slice(2) : encryptedHex;
    const encryptedBytes = Buffer.from(cleanEncrypted, 'hex');
    const privateKeyBuffer = Buffer.from(cleanPrivateKey, 'hex');

    let eccrypto;
    try {
      eccrypto = require('eccrypto');
    } catch (e) {
      const eccryptoModule = await import('eccrypto');
      eccrypto = eccryptoModule.default || eccryptoModule;
    }

    const opts = parseEciesSerialized(encryptedBytes);
    if (!opts) {
      throw new Error(
        'ECIES ciphertext has wrong length or format. Expected serialized [ephemPublicKey][iv][ciphertext][mac].'
      );
    }
    const decryptedBuffer = await eccrypto.decrypt(privateKeyBuffer, opts);
    return decryptedBuffer.toString('utf8');
  } catch (error) {
    console.error('ECIES decryption error:', error);
    throw new Error(`Failed to decrypt data: ${error.message}`);
  }
};

/**
 * Decrypt a base64-encoded _byteString field
 * @param {string} base64Encrypted - Base64-encoded encrypted data
 * @param {string} privateKeyHex - SECP256k1 private key in hex format
 * @returns {Promise<string>} Decrypted plaintext
 */
export const decryptByteString = async (base64Encrypted, privateKeyHex) => {
  try {
    // Decode base64 to get hex-encoded encrypted data
    const encryptedBuffer = Buffer.from(base64Encrypted, 'base64');
    const encryptedHex = encryptedBuffer.toString('hex');
    
    // Decrypt using ECIES
    return await decryptEciesData(encryptedHex, privateKeyHex);
  } catch (error) {
    throw new Error(`Failed to decrypt byteString: ${error.message}`);
  }
};

/**
 * Get raw bytes from a _byteString field (may be string base64, Firestore Blob, or Uint8Array)
 * @param {*} byteStringField - value of _byteString from document
 * @returns {Buffer|null}
 */
function bytesFromByteStringField(byteStringField) {
  if (!byteStringField) return null;
  // Plain string (base64)
  if (typeof byteStringField === 'string') {
    try {
      return Buffer.from(byteStringField, 'base64');
    } catch (e) {
      return null;
    }
  }
  // Firestore Bytes: toUint8Array() is reliable in browser
  if (typeof byteStringField.toUint8Array === 'function') {
    try {
      const arr = byteStringField.toUint8Array();
      if (arr && arr.length) return Buffer.from(arr);
    } catch (e) {
      // fall through
    }
  }
  // Firestore Blob or object with toBase64()
  if (typeof byteStringField.toBase64 === 'function') {
    try {
      return Buffer.from(byteStringField.toBase64(), 'base64');
    } catch (e) {
      return null;
    }
  }
  // Some SDKs expose UTF-8 as .binaryString (e.g. devtools)
  if (byteStringField.binaryString && typeof byteStringField.binaryString === 'string') {
    return Buffer.from(byteStringField.binaryString, 'utf8');
  }
  // Iterable (e.g. some Bytes implementations)
  if (byteStringField && typeof byteStringField[Symbol.iterator] === 'function') {
    try {
      return Buffer.from(Array.from(byteStringField));
    } catch (e) {
      // fall through
    }
  }
  // Uint8Array or ArrayBuffer
  if (byteStringField instanceof Uint8Array) {
    return Buffer.from(byteStringField);
  }
  if (byteStringField instanceof ArrayBuffer) {
    return Buffer.from(byteStringField);
  }
  // Object with bytes / _bytes (some SDKs)
  if (byteStringField.bytes && (byteStringField.bytes instanceof Uint8Array || Buffer.isBuffer(byteStringField.bytes))) {
    return Buffer.isBuffer(byteStringField.bytes) ? byteStringField.bytes : Buffer.from(byteStringField.bytes);
  }
  return null;
}

/**
 * True when object looks like Go json.Marshal(dkRequestSuccess) (snake_case or PascalCase).
 */
function isDkRequestSuccessShape(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  return (
    raw.auditor_identity != null ||
    raw.AuditorIdentity != null ||
    raw.chain_response != null ||
    raw.ChainResponse != null ||
    raw.decryption_key != null ||
    raw.DecryptionKey != null ||
    raw.decryption_keys != null ||
    raw.DecryptionKeys != null
  );
}

/**
 * Extract ECIES ciphertext from dkRequestSuccess (decryption_key + chain_response keyshares).
 * @param {Object} dkSuccess
 * @returns {{ encryptedHex: string } | { encryptedBytes: Buffer } | null}
 */
function extractCiphertextFromDkRequestSuccess(dkSuccess) {
  if (!dkSuccess || typeof dkSuccess !== 'object') return null;

  const rawKey = dkSuccess.DecryptionKey ?? dkSuccess.decryption_key;
  if (rawKey != null && typeof rawKey === 'string' && rawKey.trim() !== '') {
    const ciphertextStr = extractCiphertextFromDecryptionKeyValue(rawKey) || rawKey.trim();
    const out = normalizeCiphertextString(ciphertextStr);
    if (out) return out;
  }

  let chainResp = dkSuccess.ChainResponse ?? dkSuccess.chain_response;
  if (chainResp == null) return null;
  if (typeof chainResp === 'string') {
    try {
      chainResp = JSON.parse(chainResp);
    } catch (e) {
      return null;
    }
  }

  const shares = chainResp?.private_keyshares;
  if (Array.isArray(shares)) {
    for (const share of shares) {
      const hexVal = share?.encrypted_keyshare_value;
      if (typeof hexVal === 'string' && hexVal.length >= 64 && /^[0-9a-fA-F]+$/i.test(hexVal)) {
        const out = normalizeCiphertextString(hexVal);
        if (out) return out;
      }
    }
  }

  const fromWalk = findDecryptionKeyCiphertextInObject(chainResp);
  if (fromWalk) {
    const inner = extractCiphertextFromDecryptionKeyValue(fromWalk) || fromWalk;
    return normalizeCiphertextString(inner);
  }

  return null;
}

/**
 * Parse a keyshare index (encrypted_keyshare_index) to a bigint x-coordinate.
 * Accepts number or string. Returns null when it cannot be parsed.
 */
function parseKeyshareIndex(idx) {
  if (idx == null) return null;
  if (typeof idx === 'number' && Number.isInteger(idx)) return BigInt(idx);
  if (typeof idx === 'bigint') return idx;
  if (typeof idx === 'string') {
    const trimmed = idx.trim();
    if (/^-?\d+$/.test(trimmed)) {
      try {
        return BigInt(trimmed);
      } catch (e) {
        return null;
      }
    }
  }
  return null;
}

/**
 * Extract the list of VSS keyshares (ciphertext + index) from a dkRequestSuccess record.
 *
 * The backend now persists success records with a `decryption_keys` array (even a single
 * key is stored this way):
 *   { decryption_keys: [ { encrypted_keyshare_value, encrypted_keyshare_index }, ... ] }
 *
 * Falls back to the legacy singular `decryption_key` and `chain_response.private_keyshares`.
 *
 * @param {Object} dkSuccess
 * @returns {Array<{ index: (bigint|null), cipher: ({encryptedHex: string}|{encryptedBytes: Buffer}) }>}
 */
function extractKeysharesFromDkRequestSuccess(dkSuccess) {
  if (!dkSuccess || typeof dkSuccess !== 'object') return [];

  const readShares = (arr) => {
    const out = [];
    if (!Array.isArray(arr)) return out;
    for (const entry of arr) {
      if (!entry || typeof entry !== 'object') continue;
      const hexVal = entry.encrypted_keyshare_value ?? entry.EncryptedKeyshareValue;
      if (typeof hexVal !== 'string' || hexVal.trim() === '') continue;
      const cipher = normalizeCiphertextString(
        extractCiphertextFromDecryptionKeyValue(hexVal) || hexVal
      );
      if (!cipher) continue;
      const rawIdx = entry.encrypted_keyshare_index ?? entry.EncryptedKeyshareIndex;
      out.push({ index: parseKeyshareIndex(rawIdx), cipher });
    }
    return out;
  };

  // 1. New format: top-level decryption_keys array.
  let keys = dkSuccess.DecryptionKeys ?? dkSuccess.decryption_keys;
  if (typeof keys === 'string') {
    try {
      keys = JSON.parse(keys);
    } catch (e) {
      keys = null;
    }
  }
  const fromArray = readShares(keys);
  if (fromArray.length > 0) return fromArray;

  // 2. Legacy singular decryption_key (raw ciphertext or JSON wrapping keyshares).
  const rawKey = dkSuccess.DecryptionKey ?? dkSuccess.decryption_key;
  if (rawKey != null && typeof rawKey === 'string' && rawKey.trim() !== '') {
    const ciphertextStr = extractCiphertextFromDecryptionKeyValue(rawKey) || rawKey.trim();
    const cipher = normalizeCiphertextString(ciphertextStr);
    if (cipher) return [{ index: null, cipher }];
  }

  // 3. Legacy chain_response.private_keyshares.
  let chainResp = dkSuccess.ChainResponse ?? dkSuccess.chain_response;
  if (typeof chainResp === 'string') {
    try {
      chainResp = JSON.parse(chainResp);
    } catch (e) {
      chainResp = null;
    }
  }
  const fromChain = readShares(chainResp?.private_keyshares);
  if (fromChain.length > 0) return fromChain;

  return [];
}

/**
 * Extract all VSS keyshares (ciphertext + index) from a kv entry.
 * @param {Object} kvEntryData - result.data from checkExistingKeyRequest
 * @returns {Array<{ index: (bigint|null), cipher: object }>}
 */
export function getKeysharesFromKvEntry(kvEntryData) {
  const dkSuccess = getDkRequestSuccessFromKvEntry(kvEntryData);
  if (!dkSuccess) return [];
  return extractKeysharesFromDkRequestSuccess(dkSuccess);
}

/**
 * Get dkRequestSuccess object from kv entry.
 * Stored value is json.Marshal(dkRequestSuccess) with ChainResponse (respJSON).
 * @param {Object} kvEntryData - result.data from checkExistingKeyRequest
 * @returns {Object|null} dkRequestSuccess { auditor_identity, identity, chain_response, decryption_key, ... } or null
 */
function getDkRequestSuccessFromKvEntry(kvEntryData) {
  if (!kvEntryData) return null;
  let raw = kvEntryData.value || kvEntryData.v;
  // Firestore may give value as object already (parsed dkRequestSuccess)
  if (raw && isDkRequestSuccessShape(raw)) {
    return raw;
  }
  // Or as JSON string
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  // Value may be Firestore Bytes or { _byteString: Bytes } - decode and parse
  if (raw && typeof raw === 'object') {
    const valueBytes = bytesFromByteStringField(raw._byteString ?? raw);
    if (valueBytes) {
      try {
        const str = valueBytes.toString('utf8');
        if (str.startsWith('{')) return JSON.parse(str);
      } catch (e) {
        // ignore
      }
    }
  }
  // Or entire doc stored in top-level _byteString (base64 of json.Marshal(suc))
  const topBytes = bytesFromByteStringField(kvEntryData._byteString);
  if (topBytes) {
    try {
      const str = topBytes.toString('utf8');
      if (str.startsWith('{')) return JSON.parse(str);
    } catch (e) {
      // ignore
    }
  }
  return null;
}

/**
 * Best-effort scan of JSON for a non-empty string field whose key name looks like a decryption key.
 * Mirrors Go hasDecryptionKey: key (lowercase) contains "decryption" and "key", value is non-empty string.
 * @param {any} obj - Parsed ChainResponse (object or array)
 * @returns {string|null} The ciphertext string (hex or base64) or null
 */
function findDecryptionKeyCiphertextInObject(obj) {
  if (obj == null) return null;
  function walk(x) {
    if (x == null) return null;
    if (typeof x === 'object' && !Array.isArray(x)) {
      for (const k of Object.keys(x)) {
        const lk = k.toLowerCase();
        if (lk.includes('decryption') && lk.includes('key')) {
          const val = x[k];
          if (typeof val === 'string' && val.trim() !== '') return val;
        }
        const found = walk(x[k]);
        if (found) return found;
      }
      return null;
    }
    if (Array.isArray(x)) {
      for (const item of x) {
        const found = walk(item);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(obj);
}

/**
 * Normalize ciphertext string (hex or base64) to { encryptedHex } or { encryptedBytes }.
 */
function normalizeCiphertextString(str) {
  if (typeof str !== 'string' || str.length < 32) return null;
  const trimmed = str.trim();
  const hex = /^[0-9a-fA-F]+$/.test(trimmed) ? trimmed : (trimmed.startsWith('0x') && /^0x[0-9a-fA-F]+$/.test(trimmed) ? trimmed.slice(2) : null);
  if (hex && trimmed.length >= 64) return { encryptedHex: hex };
  try {
    const buf = Buffer.from(trimmed, 'base64');
    if (buf.length >= 81) return { encryptedBytes: buf };
  } catch (e) {
    // not base64
  }
  return null;
}

/**
 * If DecryptionKey is stored as JSON (e.g. [{"private_keyshares":[{"encrypted_keyshare_value":"hex..."}]}]),
 * parse it and return the actual ECIES ciphertext (encrypted_keyshare_value hex string), not the whole JSON.
 * Logs the extracted value for verification.
 * @param {string} str - Value of DecryptionKey (may be raw ciphertext or JSON wrapping it)
 * @returns {string|null} The inner ciphertext string (hex), or str if not JSON, or null
 */
function extractCiphertextFromDecryptionKeyValue(str) {
  if (typeof str !== 'string' || str.length < 2) return null;
  const trimmed = str.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return trimmed; // already raw ciphertext
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return trimmed;
  }

  // Explicit structure: [{"private_keyshares":[{"encrypted_keyshare_index":"1","encrypted_keyshare_value":"hex..."}],"requester":"..."}]
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const shares = item?.private_keyshares;
      if (Array.isArray(shares)) {
        for (const share of shares) {
          const hexVal = share?.encrypted_keyshare_value;
          if (typeof hexVal === 'string' && hexVal.length >= 64 && /^[0-9a-fA-F]+$/i.test(hexVal)) {
            const preview = hexVal.length > 80 ? `${hexVal.slice(0, 40)}...${hexVal.slice(-20)}` : hexVal;
            console.log('[extractCiphertextFromDecryptionKeyValue] Extracted encrypted_keyshare_value:', {
              length: hexVal.length,
              preview,
              fullLength: hexVal.length
            });
            console.log('[extractCiphertextFromDecryptionKeyValue] Full encrypted_keyshare_value (for verification):', hexVal);
            return hexVal;
          }
        }
      }
    }
  }

  // Fallback: walk for any known ciphertext field
  const ciphertextFields = ['encrypted_keyshare_value', 'encrypted_keyshare', 'encryptedKeyshare', 'ciphertext', 'key', 'decryption_key', 'decryptionKey'];
  function findCiphertext(x) {
    if (x == null) return null;
    if (typeof x === 'string') {
      if (x.length >= 64 && (/^[0-9a-fA-F]+$/i.test(x) || (x.startsWith('0x') && /^0x[0-9a-fA-F]+$/i.test(x)))) return x;
      try {
        const b = Buffer.from(x, 'base64');
        if (b.length >= 81) return x;
      } catch (e) {}
      return null;
    }
    if (typeof x === 'object' && !Array.isArray(x)) {
      for (const name of ciphertextFields) {
        const v = x[name];
        if (typeof v === 'string' && v.length >= 64) return v;
      }
      for (const k of Object.keys(x)) {
        const found = findCiphertext(x[k]);
        if (found) return found;
      }
      return null;
    }
    if (Array.isArray(x)) {
      for (const item of x) {
        const found = findCiphertext(item);
        if (found) return found;
      }
    }
    return null;
  }
  const found = findCiphertext(parsed);
  if (found && typeof found === 'string' && found.length >= 64) {
    const preview = found.length > 80 ? `${found.slice(0, 40)}...${found.slice(-20)}` : found;
    console.log('[extractCiphertextFromDecryptionKeyValue] Extracted ciphertext (fallback walk):', { length: found.length, preview });
    return found;
  }
  return found;
}

/**
 * Extract ciphertext from kv entry data (from checkExistingKeyRequest result.data).
 * Entry value is dkRequestSuccess (ChainResponse = API response JSON). The decryption key field
 * in ChainResponse is the ECIES ciphertext; decrypt with auditor key to get IBE decryption key.
 * @param {Object} kvEntryData - result.data from checkExistingKeyRequest
 * @returns {{ encryptedHex: string } | { encryptedBytes: Buffer } | null} Ciphertext for ECIES decrypt, or null
 */
export function getCiphertextFromKvEntry(kvEntryData) {
  if (!kvEntryData) return null;

  const toHex = (s) => {
    if (typeof s !== 'string') return null;
    const t = s.trim();
    if (t.length < 32) return null;
    if (/^[0-9a-fA-F]+$/.test(t)) return t;
    if (t.startsWith('0x') && /^0x[0-9a-fA-F]+$/.test(t)) return t.slice(2);
    return null;
  };

  const processDecoded = (decoded) => {
    if (!decoded || decoded.length < 32) return null;
    const asString = decoded.toString('utf8');
    if (asString.startsWith('{') || asString.startsWith('[')) {
      try {
        const parsed = JSON.parse(asString);
        if (isDkRequestSuccessShape(parsed)) {
          const fromDk = extractCiphertextFromDkRequestSuccess(parsed);
          if (fromDk) return fromDk;
        }
        const hex = parsed.encrypted_keyshare || parsed.encryptedKeyshare || parsed.ciphertext || parsed.key;
        if (hex && typeof hex === 'string') {
          const h = toHex(hex);
          if (h) return { encryptedHex: h };
        }
        const rawKey = parsed.DecryptionKey ?? parsed.decryption_key;
        if (rawKey && typeof rawKey === 'string') {
          const ciphertextStr = extractCiphertextFromDecryptionKeyValue(rawKey) || rawKey;
          const out = normalizeCiphertextString(ciphertextStr);
          if (out) return out;
        }
      } catch (e) {
        // fall through
      }
      // Never treat UTF-8 JSON as raw ECIES bytes
      return null;
    }
    return { encryptedBytes: decoded };
  };

  // 1. dkRequestSuccess (Go compliance-calibrator success record)
  const dkSuccess = getDkRequestSuccessFromKvEntry(kvEntryData);
  if (dkSuccess) {
    const fromDk = extractCiphertextFromDkRequestSuccess(dkSuccess);
    if (fromDk) return fromDk;
  }

  // 2. Top-level _byteString (legacy or alternate storage)
  const topBytes = bytesFromByteStringField(kvEntryData._byteString);
  if (topBytes) {
    const out = processDecoded(topBytes);
    if (out) return out;
  }

  // 3. value / v: string (hex) or object with known fields
  const v = kvEntryData.value || kvEntryData.v;
  if (typeof v === 'string') {
    const hex = toHex(v);
    if (hex) return { encryptedHex: hex };
  }
  if (v && typeof v === 'object') {
    const hex = v.encrypted_keyshare || v.encryptedKeyshare || v.ciphertext || v.key;
    if (hex && typeof hex === 'string') {
      const h = toHex(hex);
      if (h) return { encryptedHex: h };
    }
    const nestedBytes = bytesFromByteStringField(v._byteString);
    if (nestedBytes) {
      const out = processDecoded(nestedBytes);
      if (out) return out;
    }
    for (const key of Object.keys(v)) {
      const val = v[key];
      if (typeof val === 'string') {
        const h = toHex(val);
        if (h && val.length >= 64) return { encryptedHex: h };
      }
    }
  }

  return null;
}

/**
 * Decrypt an ECIES ciphertext string (hex or base64) with the auditor SECP256k1 private key.
 * @param {string} ciphertextStr - Hex or base64 ciphertext (e.g. encrypted_keyshare_value)
 * @param {string} privateKeyHex - Auditor private key (64 hex chars)
 * @returns {Promise<string>} Plaintext IBE decryption key
 */
export async function decryptCiphertextString(ciphertextStr, privateKeyHex) {
  const cipher = normalizeCiphertextString(
    extractCiphertextFromDecryptionKeyValue(ciphertextStr) || ciphertextStr
  );
  if (!cipher) {
    throw new Error('Invalid ciphertext format');
  }
  return decryptCiphertextMaterial(cipher, privateKeyHex);
}

/**
 * Decrypt raw ciphertext material with auditor SECP256k1 private key.
 * @param {{ encryptedHex?: string, encryptedBytes?: Buffer }} cipher
 * @param {string} privateKeyHex
 * @returns {Promise<string>}
 */
async function decryptCiphertextMaterial(cipher, privateKeyHex) {
  let cleanKey = (privateKeyHex.startsWith('0x') ? privateKeyHex.slice(2) : privateKeyHex).trim();
  if (cleanKey.length === 66 && /^04[0-9a-fA-F]{64}$/.test(cleanKey)) {
    cleanKey = cleanKey.slice(2);
  }
  if (cleanKey.length !== 64 || !/^[0-9a-fA-F]+$/.test(cleanKey)) {
    throw new Error(`Invalid private key: expected 64 hex chars, got ${cleanKey.length}`);
  }
  const privateKeyBuffer = Buffer.from(cleanKey, 'hex');

  const raw = cipher.encryptedHex
    ? Buffer.from(cipher.encryptedHex.replace(/^0x/, ''), 'hex')
    : cipher.encryptedBytes;

  if (!raw || raw.length < 81) throw new Error('Invalid ciphertext format');

  console.log('[decryptKeyFromKvEntry] === ECIES DECRYPT DEBUG ===');
  console.log('[decryptKeyFromKvEntry] Private key hex:', cleanKey);
  console.log('[decryptKeyFromKvEntry] Private key buffer length:', privateKeyBuffer.length, 'bytes');
  console.log('[decryptKeyFromKvEntry] Ciphertext source:', cipher.encryptedHex ? 'hex' : 'bytes');
  console.log('[decryptKeyFromKvEntry] Ciphertext total length:', raw.length, 'bytes');
  console.log('[decryptKeyFromKvEntry] Ciphertext first 20 bytes:', raw.subarray(0, 20).toString('hex'));
  console.log('[decryptKeyFromKvEntry] isBtcecFormat:', isBtcecFormat(raw));
  if (isBtcecFormat(raw)) {
    // Log btcec structure breakdown: IV(16) | PubKey(70) | Ciphertext | HMAC(32)
    const iv = raw.subarray(0, 16);
    const ephemPubArea = raw.subarray(16, 86); // 70 bytes
    const hmac = raw.subarray(raw.length - 32);
    const ctLen = raw.length - 16 - 70 - 32;
    console.log('[decryptKeyFromKvEntry] btcec IV (16):', iv.toString('hex'));
    console.log('[decryptKeyFromKvEntry] btcec ephemPubKey area (70):', ephemPubArea.toString('hex'));
    console.log('[decryptKeyFromKvEntry] btcec ciphertext length:', ctLen);
    console.log('[decryptKeyFromKvEntry] btcec HMAC (32):', hmac.toString('hex'));
  }

  let decryptedStr;
  if (isBtcecFormat(raw)) {
    const decrypted = await decryptBtcec(privateKeyBuffer, raw);
    decryptedStr = decrypted.toString('utf8');
  } else if (cipher.encryptedHex) {
    decryptedStr = await decryptEciesData(cipher.encryptedHex, privateKeyHex);
  } else if (cipher.encryptedBytes) {
    const opts = parseEciesSerialized(cipher.encryptedBytes);
    if (!opts) {
      throw new Error(
        'ECIES ciphertext has wrong length or format. Expected serialized [ephemPublicKey][iv][ciphertext][mac].'
      );
    }
    let eccrypto;
    try {
      eccrypto = require('eccrypto');
    } catch (e) {
      const m = await import('eccrypto');
      eccrypto = m.default || m;
    }
    const decrypted = await eccrypto.decrypt(privateKeyBuffer, opts);
    decryptedStr = decrypted.toString('utf8');
  } else {
    throw new Error('Invalid ciphertext format');
  }

  console.log('[decryptCiphertextMaterial] Decrypted key (before IBE):', decryptedStr);
  return decryptedStr;
}

/**
 * Decrypt the key from kv entry data using auditor's SECP256k1 private key
 * (Ciphertext was encrypted with btcec.Encrypt using the auditor's public key)
 * @param {Object} kvEntryData - result.data from checkExistingKeyRequest
 * @param {string} privateKeyHex - Auditor's SECP256k1 private key (hex, with or without 0x)
 * @returns {Promise<string>} Decrypted key (plaintext) to use as IBE/decryption key
 */
export async function decryptKeyFromKvEntry(kvEntryData, privateKeyHex) {
  const cipher = getCiphertextFromKvEntry(kvEntryData);
  if (!cipher) {
    const keys = kvEntryData && typeof kvEntryData === 'object' ? Object.keys(kvEntryData) : [];
    const valueKeys = kvEntryData && kvEntryData.value && typeof kvEntryData.value === 'object' ? Object.keys(kvEntryData.value) : [];
    throw new Error(
      'No ciphertext found in this entry. Expected dkRequestSuccess (decryption_key / chain_response) or encrypted_keyshare. ' +
      `Document keys: ${keys.join(', ') || 'none'}. ` +
      (valueKeys.length ? `Value keys: ${valueKeys.join(', ')}.` : '')
    );
  }

  return decryptCiphertextMaterial(cipher, privateKeyHex);
}

/**
 * Decrypt the IBE private key from a kv entry, supporting multiple VSS keyshares.
 *
 * New backend records store one or more shares under `decryption_keys[]`. Each share's
 * `encrypted_keyshare_value` is an ECIES ciphertext (encrypted to the auditor's SECP256k1
 * public key). We decrypt every share with the auditor's private key; the plaintext of each
 * is a hex-encoded BLS12-381 G2 point. When there is more than one share we reconstruct the
 * real IBE private key via Lagrange interpolation (VSS aggregation).
 *
 * Falls back to the legacy single-ciphertext path (and an optional caller-provided
 * ciphertext) when no `decryption_keys` array is present.
 *
 * @param {Object} kvEntryData - result.data from checkExistingKeyRequest
 * @param {string} privateKeyHex - Auditor's SECP256k1 private key (hex, with or without 0x)
 * @param {string} [fallbackCiphertext] - Optional ciphertext string when the entry has no shares
 * @returns {Promise<string>} IBE private key (hex-encoded G2 point) ready for timelockDecrypt
 */
export async function decryptIbeKeyFromKvEntry(kvEntryData, privateKeyHex, fallbackCiphertext = null) {
  const shares = getKeysharesFromKvEntry(kvEntryData);

  if (!shares || shares.length === 0) {
    // Legacy: single ciphertext extracted from the entry, or a caller-provided one.
    const cipher = getCiphertextFromKvEntry(kvEntryData);
    if (cipher) {
      return decryptCiphertextMaterial(cipher, privateKeyHex);
    }
    if (fallbackCiphertext) {
      return decryptCiphertextString(fallbackCiphertext, privateKeyHex);
    }
    throw new Error(
      'No decryption keyshares found in this entry. Expected dkRequestSuccess ' +
      '(decryption_keys / decryption_key / chain_response).'
    );
  }

  console.log(`[decryptIbeKeyFromKvEntry] Found ${shares.length} keyshare(s) to decrypt`);

  // Decrypt every keyshare with the auditor's private key.
  const decryptedShares = [];
  for (let i = 0; i < shares.length; i++) {
    const { index, cipher } = shares[i];
    const keyHex = (await decryptCiphertextMaterial(cipher, privateKeyHex)).trim();
    console.log(`[decryptIbeKeyFromKvEntry] Decrypted keyshare index=${index != null ? index.toString() : 'n/a'} (${i + 1}/${shares.length})`);
    decryptedShares.push({ index, keyHex });
  }

  // Single share: it is the IBE private key directly (no interpolation needed).
  if (decryptedShares.length === 1) {
    return decryptedShares[0].keyHex;
  }

  // Multiple shares: reconstruct via Lagrange interpolation over G2.
  const missingIndex = decryptedShares.find((s) => s.index == null);
  if (missingIndex) {
    throw new Error(
      'Cannot aggregate keyshares: one or more shares are missing encrypted_keyshare_index.'
    );
  }
  console.log('[decryptIbeKeyFromKvEntry] Aggregating keyshares via Lagrange interpolation (VSS)');
  const aggregated = aggregateKeyshares(decryptedShares);
  console.log('[decryptIbeKeyFromKvEntry] Aggregated IBE key:', aggregated);
  return aggregated;
}

/**
 * Get and decrypt all three entries for a decryption key request
 * @param {string} auditorIdentity - Auditor UID
 * @param {string} userAddress - User's wallet address
 * @param {string} privateKeyHex - Auditor's SECP256k1 private key
 * @returns {Promise<Array>} Array of decrypted entry objects
 */
export const getAndDecryptKvEntries = async (auditorIdentity, userAddress, privateKeyHex) => {
  try {
    // Get the three entries
    const entries = await getKvEntriesForRequest(auditorIdentity, userAddress);
    
    if (entries.length === 0) {
      throw new Error('No kv entries found for this decryption key request');
    }
    
    if (entries.length !== 3) {
      console.warn(`Expected 3 entries, found ${entries.length}`);
    }
    
    // Decrypt each entry
    const decryptedEntries = await Promise.all(
      entries.map(async (entry, index) => {
        try {
          if (!entry._byteString) {
            throw new Error(`Entry ${index + 1} missing _byteString field`);
          }
          
          const decrypted = await decryptByteString(entry._byteString, privateKeyHex);
          
          return {
            ...entry,
            decrypted,
            decryptionSuccess: true
          };
        } catch (error) {
          console.error(`Failed to decrypt entry ${index + 1}:`, error);
          return {
            ...entry,
            decrypted: null,
            decryptionError: error.message,
            decryptionSuccess: false
          };
        }
      })
    );
    
    return decryptedEntries;
  } catch (error) {
    throw new Error(`Failed to get and decrypt kv entries: ${error.message}`);
  }
};
